import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import styles from './styles.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsAlt, faArrowsAltH, faArrowsAltV } from '@fortawesome/free-solid-svg-icons';

const Textarea = ({
  value,
  onChange,
  placeholder = 'Type here...',
  resize = 'both', // Can be 'horizontal', 'vertical', 'both', or 'none'
  minHeight = "100%",
  maxHeight = "100%",
  minWidth = "100%",
  maxWidth = "100%",
  minCharacters = 0,
  maxCharacters = 1000,
  showComponent = true,
  scrollableParentClass = '', // ID or class of the scrollable parent
}) => {
  const [characterCount, setCharacterCount] = useState(value.length);
  const [resizeDimensions, setResizeDimensions] = useState({ width: 0, height: 0 });
  const [isResizing, setIsResizing] = useState(false);

  const containerRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const scrollableParentRef = useRef(null);

  useEffect(() => {
    setCharacterCount(value.length);
  }, [value]);

  useEffect(() => {
    if (scrollableParentClass) {
      scrollableParentRef.current = document.querySelector(scrollableParentClass);
    }
  }, [scrollableParentClass]);

  // Calculate initial dimensions (handles both percentages and pixel values)
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const { width: parentWidth, height: parentHeight } = container.parentElement.getBoundingClientRect();

      const initialWidth = calculateDimension(minWidth, parentWidth);
      const initialHeight = calculateDimension(minHeight, parentHeight);

      setResizeDimensions({ width: initialWidth, height: initialHeight });
    }
  }, [minWidth, minHeight]);

  const disableParentScroll = () => {
    if (scrollableParentRef.current) {
      scrollableParentRef.current.style.overflow = 'hidden';
    }
  };

  const enableParentScroll = () => {
    if (scrollableParentRef.current) {
      scrollableParentRef.current.style.overflow = '';
    }
  };

  const getResizeIcon = () => {
    switch (resize) {
      case 'horizontal':
        return faArrowsAltH;
      case 'vertical':
        return faArrowsAltV;
      case 'both':
        return faArrowsAlt;
      case 'none':
      default:
        return null;
    }
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    disableParentScroll();
    setIsResizing(true);

    const { width, height } = containerRef.current.getBoundingClientRect();
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;

    resizeStartRef.current = {
      x: clientX,
      y: clientY,
      width,
      height,
    };

    const moveEvent = e.type.includes('mouse') ? 'mousemove' : 'touchmove';
    const endEvent = e.type.includes('mouse') ? 'mouseup' : 'touchend';

    window.addEventListener(moveEvent, handleResize);
    window.addEventListener(endEvent, handleResizeEnd);
  };

  // Calculate the actual dimension (handles both percentages and pixel values)
  const calculateDimension = (value, parentSize) => {
    if (value.endsWith('%')) {
      return (parseFloat(value) / 100) * parentSize; // Convert percentage to absolute value
    }
    return parseFloat(value) || 0; // Default to pixel value
  };

  const handleResize = (e) => {
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;

    const deltaX = clientX - resizeStartRef.current.x;
    const deltaY = clientY - resizeStartRef.current.y;

    const parent = containerRef.current.parentElement;
    const { width: parentWidth, height: parentHeight } = parent.getBoundingClientRect();

    const validatedMinWidth = calculateDimension(minWidth, parentWidth);
    const validatedMaxWidth = calculateDimension(maxWidth, parentWidth);
    const validatedMinHeight = calculateDimension(minHeight, parentHeight);
    const validatedMaxHeight = calculateDimension(maxHeight, parentHeight);

    let newWidth = resizeStartRef.current.width;
    let newHeight = resizeStartRef.current.height;

    if (resize === 'horizontal' || resize === 'both') {
      newWidth = Math.min(Math.max(newWidth + deltaX, validatedMinWidth), validatedMaxWidth);
    }

    if (resize === 'vertical' || resize === 'both') {
      newHeight = Math.min(Math.max(newHeight + deltaY, validatedMinHeight), validatedMaxHeight);
    }

    setResizeDimensions({
      width: newWidth,
      height: newHeight,
    });
  };

  const handleResizeEnd = () => {
    enableParentScroll();
    setIsResizing(false);

    window.removeEventListener('mousemove', handleResize);
    window.removeEventListener('mouseup', handleResizeEnd);
    window.removeEventListener('touchmove', handleResize);
    window.removeEventListener('touchend', handleResizeEnd);
  };

  const handleTextChange = (e) => {
    const text = e.target.value;

    if (text.length <= maxCharacters) {
      setCharacterCount(text.length);
      onChange(text);
    }
  };

  return (
    <motion.div
      ref={containerRef}
      className={styles.textareaContainer}
      animate={{
        width: resizeDimensions.width,
        height: resizeDimensions.height,
      }}
      transition={{ duration: 0 }}
      style={{
        minWidth,
        maxWidth,
        minHeight,
        maxHeight,
        cursor: isResizing ? 'grabbing' : 'auto', // Change cursor during resizing
        display: showComponent ? 'block' : 'none',
      }}
    >
      <textarea
        value={value}
        onChange={handleTextChange}
        placeholder={placeholder}
        className={styles.textarea}
        style={{
          resize: 'none',
          width: '100%',
          height: '100%',
        }}
      />
      {resize !== 'none' && (
        <div
          className={styles.resizer}
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          role="button"
          tabIndex={0}
          style={{
            cursor: isResizing ? 'grabbing' : 'grab',
          }}
        >
          <FontAwesomeIcon icon={getResizeIcon()} />
        </div>
      )}
      <p className={styles.characterCount}>
        {characterCount < minCharacters
          ? `${minCharacters - characterCount} more characters needed.`
          : `${characterCount} / ${maxCharacters}`}
      </p>
    </motion.div>
  );
};

export default Textarea;
